import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Sparkles, Send, Bot, User as UserIcon, Loader2, Rocket, Timer, Route,
    Ship, Anchor, TrendingDown, DollarSign, CheckCircle2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Demo rate data (used when no user override)
const DEFAULT_LANE = {
    origin: 'New York',
    originCode: 'USNYC',
    destination: 'Liverpool',
    destinationCode: 'GBLIV',
    container: '40HQ',
    commodity: 'Newsprint',
};

const CONTAINER_TYPES = ['20GP', '40GP', '40HC', '40HQ', '45HC', 'Reefer 20', 'Reefer 40'];

const DEMO_RATES = [
    { type: 'Contract', carrier: 'Contract', rate: 750, transitDays: 14, reliability: 98 },
    { type: 'Spot',     carrier: 'CMA CGM',  rate: 610, transitDays: 11, reliability: 89 },
    { type: 'ShipEz',   carrier: 'MSC',      rate: 680, transitDays: 13, reliability: 95 },
];

// Question flow — exactly 3 questions, multiple choice where applicable
const QUESTIONS = [
    {
        id: 'urgency',
        prompt: "First — what's your shipment urgency?",
        options: ['Low', 'Medium', 'High'],
    },
    {
        id: 'priority',
        prompt: "Got it. What matters most to you?",
        options: ['Lowest Cost', 'Fastest Transit', 'Balanced'],
    },
    {
        id: 'carrier',
        prompt: "Last one — any preferred carrier? (Type a carrier name or tap 'No preference')",
        options: ['No preference', 'MSC', 'CMA CGM', 'Maersk'],
        allowFreeText: true,
    },
];

// Ranking logic — returns rates with `tags[]` + `savings` + `recommended: true`
const rankRates = (rates, answers) => {
    const cheapest = [...rates].sort((a, b) => a.rate - b.rate)[0];
    const fastest  = [...rates].sort((a, b) => a.transitDays - b.transitDays)[0];
    const contract = rates.find(r => r.type === 'Contract');

    let recommended = cheapest;
    const priority = (answers.priority || '').toLowerCase();
    const urgency  = (answers.urgency  || '').toLowerCase();
    const carrierPref = (answers.carrier || '').toLowerCase();

    if (priority.includes('fastest')) {
        recommended = fastest;
    } else if (priority.includes('lowest') || priority.includes('cost')) {
        recommended = cheapest;
    } else {
        // Balanced — score by (rate/maxRate) + (transit/maxTransit)
        const maxRate    = Math.max(...rates.map(r => r.rate));
        const maxTransit = Math.max(...rates.map(r => r.transitDays));
        recommended = [...rates].sort((a, b) => {
            const sa = a.rate / maxRate + a.transitDays / maxTransit;
            const sb = b.rate / maxRate + b.transitDays / maxTransit;
            return sa - sb;
        })[0];
    }

    // Urgency override — High urgency trumps "lowest cost" in favor of fastest if transit delta > 2 days
    if (urgency === 'high' && recommended.transitDays > fastest.transitDays + 2) {
        recommended = fastest;
    }
    // Low urgency — prefer cheapest even in Balanced mode
    if (urgency === 'low' && priority.includes('balanced')) {
        recommended = cheapest;
    }

    // Preferred carrier override — if user's carrier choice matches, promote it
    if (carrierPref && !['', 'no preference'].includes(carrierPref)) {
        const match = rates.find(r => r.carrier.toLowerCase().includes(carrierPref));
        if (match) recommended = match;
    }

    return rates.map(r => ({
        ...r,
        tags: [
            r.rate === cheapest.rate ? 'Best Price' : null,
            r.transitDays === fastest.transitDays ? 'Fastest' : null,
            r === recommended ? 'Recommended' : null,
        ].filter(Boolean),
        savings: contract && r.rate < contract.rate ? contract.rate - r.rate : 0,
        recommended: r === recommended,
    }));
};

// Generate plain-English recommendation based on answers + winner
const buildInsight = (ranked, answers, lane) => {
    const winner = ranked.find(r => r.recommended);
    if (!winner) return '';
    const urgency = (answers.urgency || '').toLowerCase();
    const priority = (answers.priority || '').toLowerCase();
    const carrierPref = (answers.carrier || '').trim();
    const cheapest = [...ranked].sort((a, b) => a.rate - b.rate)[0];
    const fastest  = [...ranked].sort((a, b) => a.transitDays - b.transitDays)[0];

    let reason = '';
    if (priority.includes('lowest')) {
        reason = `you flagged cost as the top priority, and ${winner.carrier}'s ${winner.type} rate at $${winner.rate}/container is the cheapest option on this lane`;
    } else if (priority.includes('fastest')) {
        reason = `you need it fast — ${winner.carrier} delivers in ${winner.transitDays} days, which is the shortest transit available`;
    } else {
        reason = `you're optimizing both cost and speed — ${winner.carrier}'s ${winner.type} at $${winner.rate}/container keeps the total cost low without adding too many days to transit`;
    }

    let urgencyNote = '';
    if (urgency === 'high') {
        urgencyNote = ` With high urgency, I've leaned toward carriers hitting ${fastest.transitDays}-day transit.`;
    } else if (urgency === 'low') {
        urgencyNote = ` Since urgency is low, saving cash beats shaving a few days off transit.`;
    }

    const carrierNote = carrierPref && !['', 'no preference'].includes(carrierPref.toLowerCase())
        ? ` I also checked your carrier preference (${carrierPref}).`
        : '';

    const savingsNote = winner.savings > 0
        ? ` That saves you $${winner.savings}/container vs the contract rate — around $${(winner.savings * 15).toLocaleString()} across a 15-container shipment.`
        : '';

    const tradeoff = winner !== cheapest
        ? ` You could save another $${(winner.rate - cheapest.rate)}/container by switching to ${cheapest.carrier} (${cheapest.type}), but transit stretches to ${cheapest.transitDays} days.`
        : (winner !== fastest
            ? ` If you need it faster, ${fastest.carrier} ships in ${fastest.transitDays} days for $${fastest.rate}/container.`
            : '');

    return `For ${lane.origin} → ${lane.destination} (${lane.container} ${lane.commodity}), ${reason}.${urgencyNote}${carrierNote}${savingsNote}${tradeoff}`.trim();
};

const ROUTING_OPTIONS = [
    {
        id: 'direct',
        label: 'Direct Port Pair (USNYC → GBLIV)',
        description: 'Direct sailing, weekly schedule.',
        transitDays: 11,
        costDelta: 0,
        ports: 'New York → Liverpool',
        highlights: ['Weekly direct service', 'Minimal handling', 'On-time ~92%'],
    },
    {
        id: 'via-rotterdam',
        label: 'Transshipment via Rotterdam',
        description: 'Routes through NLRTM — broader carrier pool, occasional cost savings.',
        transitDays: 14,
        costDelta: -35,
        ports: 'New York → Rotterdam → Liverpool',
        highlights: ['Cheaper by ~$35/container', '+3 days transit', 'Best for non-urgent cargo'],
    },
];

const RateIntelligenceAgent = () => {
    const [lane, setLane] = useState(DEFAULT_LANE);
    const [laneConfirmed, setLaneConfirmed] = useState(false);
    const [messages, setMessages] = useState(() => ([
        {
            id: 'intro',
            role: 'assistant',
            text: "Hi! I'm the Rate Intelligence Agent. First, let me know your lane — origin, destination, container type — then I'll ask 3 quick questions and recommend the best rate."
        },
        {
            id: 'lane-setup',
            role: 'assistant',
            kind: 'lane-setup',
        }
    ]));
    const [answers, setAnswers] = useState({});
    const [step, setStep] = useState(0);
    const [input, setInput] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedRateIdx, setSelectedRateIdx] = useState(null);
    const [rankedRates, setRankedRates] = useState([]);
    const [insight, setInsight] = useState('');
    const [loadingAI, setLoadingAI] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        // Scroll only within the chat container — using `block: 'nearest'` prevents the
        // browser from scrolling ancestor elements (body/layout), which would push the
        // TopBar off-screen when new messages are appended.
        if (!messagesEndRef.current) return;
        const scrollable = messagesEndRef.current.closest('[data-chat-scroll]');
        if (scrollable) {
            scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: 'smooth' });
        } else {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [messages, showResults]);

    const confirmLane = () => {
        if (!lane.origin?.trim() || !lane.destination?.trim() || !lane.container) {
            toast.error('Please fill in origin, destination and container type');
            return;
        }
        setLaneConfirmed(true);
        setMessages(prev => [
            ...prev,
            { id: 'lane-confirmed', role: 'user', text: `${lane.origin} → ${lane.destination} · ${lane.container}${lane.commodity ? ' · ' + lane.commodity : ''}` },
            {
                id: 'q-0',
                role: 'assistant',
                text: QUESTIONS[0].prompt,
                options: QUESTIONS[0].options,
                questionId: QUESTIONS[0].id,
            }
        ]);
    };

    const submitAnswer = async (value) => {
        const q = QUESTIONS[step];
        if (!q) return;

        // Append user reply + record answer
        const newAnswers = { ...answers, [q.id]: value };
        setAnswers(newAnswers);
        setMessages(prev => [...prev, { id: `u-${step}-${Date.now()}`, role: 'user', text: value }]);
        setInput('');

        const nextIdx = step + 1;
        if (nextIdx < QUESTIONS.length) {
            // Ask next question
            const nextQ = QUESTIONS[nextIdx];
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    id: `q-${nextIdx}`,
                    role: 'assistant',
                    text: nextQ.prompt,
                    options: nextQ.options,
                    questionId: nextQ.id,
                    allowFreeText: nextQ.allowFreeText,
                }]);
                setStep(nextIdx);
            }, 280);
        } else {
            // All 3 answered — rank + call LLM for insight, then show results
            setStep(nextIdx);
            setLoadingAI(true);
            const ranked = rankRates(DEMO_RATES, newAnswers);
            setRankedRates(ranked);
            setSelectedRateIdx(ranked.findIndex(r => r.recommended));

            // Offline fallback insight
            const localInsight = buildInsight(ranked, newAnswers, lane);

            // Try to enrich with LLM
            try {
                const prompt = `User answers: urgency=${newAnswers.urgency}, priority=${newAnswers.priority}, preferredCarrier=${newAnswers.carrier}. Lane: ${lane.origin}→${lane.destination} ${lane.container} ${lane.commodity || ''}. Ranked rates: ${JSON.stringify(ranked.map(r => ({ carrier: r.carrier, type: r.type, rate: r.rate, transit: r.transitDays, tags: r.tags, recommended: r.recommended })))}. In 3-4 sentences, explain why the recommended rate is best — reference user inputs, call out tradeoffs (cost vs transit vs reliability), and use plain friendly English.`;
                const res = await fetch(`${API_URL}/api/ai/rate-search/chat`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: `rate-intel-${Date.now()}`, message: prompt }),
                });
                const data = await res.json();
                setInsight((data?.reply || '').trim() || localInsight);
            } catch (err) {
                setInsight(localInsight);
            }
            setLoadingAI(false);
            setShowResults(true);
        }
    };

    const handleFreeText = () => {
        const text = input.trim();
        if (!text) return;
        submitAnswer(text);
    };

    const resetChat = () => {
        setAnswers({});
        setStep(0);
        setShowResults(false);
        setSelectedRateIdx(null);
        setRankedRates([]);
        setInsight('');
        setInput('');
        setLane(DEFAULT_LANE);
        setLaneConfirmed(false);
        setMessages([
            {
                id: 'intro',
                role: 'assistant',
                text: "Hi! I'm the Rate Intelligence Agent. First, let me know your lane — origin, destination, container type — then I'll ask 3 quick questions and recommend the best rate."
            },
            {
                id: 'lane-setup',
                role: 'assistant',
                kind: 'lane-setup',
            }
        ]);
    };

    const currentQuestion = QUESTIONS[step];
    const selectedRate = selectedRateIdx != null ? rankedRates[selectedRateIdx] : null;

    return (
        <div className="h-full overflow-y-auto bg-slate-50 dark:bg-slate-950 p-4 sm:p-6" data-testid="rate-intelligence-page">
            {/* Header */}
            <div className="max-w-7xl mx-auto mb-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 via-sky-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-indigo-500/30">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">Rate Intelligence Agent</h1>
                        <p className="text-sm text-slate-500">Mark · AI-powered rate discovery for your next shipment</p>
                    </div>
                </div>
                <button
                    onClick={resetChat}
                    className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
                    data-testid="reset-chat-btn"
                >
                    <RefreshCw className="w-4 h-4" /> Start over
                </button>
            </div>

            <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-5 lg:items-stretch">
                {/* LEFT: chat */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden flex flex-col min-h-[480px] h-[calc(100dvh-var(--navbar-height,80px)-140px)]">
                    <div className="px-5 py-3 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-indigo-500/10 via-sky-500/10 to-cyan-500/10 flex items-center gap-2">
                        <Bot className="w-5 h-5 text-indigo-500" />
                        <div>
                            <p className="font-semibold text-slate-700 dark:text-white">Chat</p>
                            <p className="text-xs text-slate-500">3 quick questions — then rates</p>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-5 space-y-4" data-testid="chat-messages" data-chat-scroll>
                        {messages.map((m, mIdx) => (
                            <div key={m.id} className={`flex items-start gap-2.5 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                                {m.role === 'assistant' ? (
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                        <Bot className="w-4 h-4 text-white" />
                                    </div>
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center flex-shrink-0">
                                        <UserIcon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                                    </div>
                                )}
                                <div className={`max-w-[85%] space-y-2 ${m.role === 'user' ? 'items-end' : 'items-start'} flex flex-col`}>
                                    {m.kind === 'lane-setup' ? (
                                        <div className="rounded-2xl rounded-tl-sm bg-slate-100 dark:bg-slate-800 p-3.5 w-full max-w-md" data-testid="lane-setup-card">
                                            <p className="text-xs uppercase tracking-wider text-indigo-500 font-semibold mb-2">Set up your lane</p>
                                            <div className="space-y-2.5">
                                                <div>
                                                    <label className="block text-[11px] font-medium text-slate-500 mb-1">Origin port / city</label>
                                                    <input
                                                        type="text"
                                                        value={lane.origin}
                                                        onChange={(e) => setLane(l => ({ ...l, origin: e.target.value }))}
                                                        disabled={laneConfirmed}
                                                        placeholder="e.g. New York"
                                                        data-testid="lane-origin"
                                                        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-medium text-slate-500 mb-1">Destination port / city</label>
                                                    <input
                                                        type="text"
                                                        value={lane.destination}
                                                        onChange={(e) => setLane(l => ({ ...l, destination: e.target.value }))}
                                                        disabled={laneConfirmed}
                                                        placeholder="e.g. Liverpool"
                                                        data-testid="lane-destination"
                                                        className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                    />
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Container type</label>
                                                        <select
                                                            value={lane.container}
                                                            onChange={(e) => setLane(l => ({ ...l, container: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            data-testid="lane-container"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        >
                                                            {CONTAINER_TYPES.map(c => <option key={c} value={c}>{c}</option>)}
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-[11px] font-medium text-slate-500 mb-1">Commodity (optional)</label>
                                                        <input
                                                            type="text"
                                                            value={lane.commodity}
                                                            onChange={(e) => setLane(l => ({ ...l, commodity: e.target.value }))}
                                                            disabled={laneConfirmed}
                                                            placeholder="e.g. Newsprint"
                                                            data-testid="lane-commodity"
                                                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 disabled:opacity-60"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            {!laneConfirmed && (
                                                <button
                                                    onClick={confirmLane}
                                                    data-testid="lane-confirm-btn"
                                                    className="mt-3 w-full px-3 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600"
                                                >
                                                    Continue
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        <div className={`px-3.5 py-2 rounded-2xl text-sm leading-relaxed ${
                                            m.role === 'user'
                                                ? 'bg-indigo-600 text-white rounded-tr-sm'
                                                : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-white rounded-tl-sm'
                                        }`}>
                                            {m.text}
                                        </div>
                                    )}
                                    {m.options && mIdx === messages.length - 1 && !showResults && (
                                        <div className="flex flex-wrap gap-2">
                                            {m.options.map((opt, i) => (
                                                <button
                                                    key={i}
                                                    onClick={() => submitAnswer(opt)}
                                                    className="px-3 py-1.5 rounded-full text-xs font-medium bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 border border-indigo-100 dark:border-indigo-500/20"
                                                    data-testid={`option-${m.questionId}-${i}`}
                                                >
                                                    {opt}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        {loadingAI && (
                            <div className="flex items-start gap-2.5">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                    <Bot className="w-4 h-4 text-white" />
                                </div>
                                <div className="px-3.5 py-2.5 rounded-2xl bg-slate-100 dark:bg-slate-800 rounded-tl-sm flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input — only active on free-text questions, disabled after results */}
                    {currentQuestion?.allowFreeText && !showResults && (
                        <div className="border-t border-slate-100 dark:border-slate-800 p-3 bg-slate-50 dark:bg-slate-900">
                            <div className="flex items-end gap-2">
                                <input
                                    type="text"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleFreeText(); } }}
                                    placeholder="Type a carrier name..."
                                    data-testid="chat-input"
                                    className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm text-slate-700 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                                />
                                <button
                                    onClick={handleFreeText}
                                    disabled={!input.trim()}
                                    className="p-2.5 rounded-xl bg-gradient-to-r from-indigo-500 to-cyan-500 hover:from-indigo-600 hover:to-cyan-600 text-white disabled:opacity-50"
                                >
                                    <Send className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* RIGHT: results panel */}
                <div className="lg:col-span-3 space-y-5 overflow-y-auto pr-1 min-h-[480px] h-[calc(100dvh-var(--navbar-height,80px)-140px)]">
                    {!showResults ? (
                        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-8 text-center" data-testid="waiting-panel">
                            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500/20 via-sky-500/20 to-cyan-500/20 mx-auto mb-4 flex items-center justify-center">
                                <Sparkles className="w-8 h-8 text-indigo-500" />
                            </div>
                            <h2 className="text-lg font-semibold text-slate-800 dark:text-white mb-1">Your rate recommendation will appear here</h2>
                            <p className="text-sm text-slate-500 max-w-md mx-auto">Answer the 3 quick questions on the left. I'll pull rates from our matrix, compare Contract · Spot · ShipEz, and recommend the best fit for your shipment.</p>
                            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-slate-400">
                                <span className="flex items-center gap-1"><Ship className="w-4 h-4" /> {lane.origin} → {lane.destination}</span>
                                <span className="flex items-center gap-1"><Anchor className="w-4 h-4" /> {lane.container} {lane.commodity}</span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Lane summary */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                                <span className="flex items-center gap-2 text-slate-700 dark:text-white font-medium">
                                    <Ship className="w-4 h-4 text-indigo-500" />
                                    {lane.origin} → {lane.destination}
                                </span>
                                <span className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                                    <Anchor className="w-4 h-4 text-slate-400" />
                                    {lane.container} · {lane.commodity || '—'}
                                </span>
                                <span className="text-xs text-slate-500">Urgency: <b className="text-slate-700 dark:text-white">{answers.urgency}</b></span>
                                <span className="text-xs text-slate-500">Priority: <b className="text-slate-700 dark:text-white">{answers.priority}</b></span>
                                <span className="text-xs text-slate-500">Carrier: <b className="text-slate-700 dark:text-white">{answers.carrier || 'Any'}</b></span>
                            </div>

                            {/* 3 Rate Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="rate-cards">
                                {rankedRates.map((r, idx) => {
                                    const isSelected = selectedRateIdx === idx;
                                    return (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, y: 12 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.08 }}
                                            onClick={() => setSelectedRateIdx(idx)}
                                            data-testid={`rate-card-${idx}`}
                                            className={`cursor-pointer relative rounded-2xl border-2 p-5 bg-white dark:bg-slate-900 shadow-sm transition-all ${
                                                isSelected
                                                    ? 'border-indigo-500 shadow-lg shadow-indigo-500/20 scale-[1.02]'
                                                    : 'border-slate-200 dark:border-slate-800 hover:border-indigo-200 dark:hover:border-indigo-500/30'
                                            }`}
                                        >
                                            {r.recommended && (
                                                <span className="absolute -top-3 left-4 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-gradient-to-r from-indigo-500 to-cyan-500 text-white shadow">
                                                    ✨ Recommended
                                                </span>
                                            )}
                                            <div className="flex items-start justify-between mb-3">
                                                <div>
                                                    <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">{r.type}</p>
                                                    <p className="text-lg font-bold text-slate-800 dark:text-white">{r.carrier}</p>
                                                </div>
                                                {isSelected && <CheckCircle2 className="w-5 h-5 text-indigo-500" />}
                                            </div>
                                            <div className="flex items-baseline gap-1 mb-3">
                                                <span className="text-3xl font-bold text-slate-800 dark:text-white">${r.rate}</span>
                                                <span className="text-xs text-slate-500">/ container</span>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
                                                <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {r.transitDays} days</span>
                                                <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> {r.reliability}% on-time</span>
                                            </div>
                                            {r.savings > 0 && (
                                                <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium mb-3">
                                                    <TrendingDown className="w-3 h-3" />
                                                    Saves ${r.savings}/container vs contract
                                                </div>
                                            )}
                                            {r.tags.length > 0 && (
                                                <div className="flex flex-wrap gap-1.5">
                                                    {r.tags.map((tag, tIdx) => {
                                                        const tagCfg = {
                                                            'Best Price': 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400',
                                                            'Fastest': 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400',
                                                            'Recommended': 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-400',
                                                        }[tag] || 'bg-slate-100 text-slate-600';
                                                        return (
                                                            <span key={tIdx} className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${tagCfg}`}>{tag}</span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </motion.div>
                                    );
                                })}
                            </div>

                            {/* AI Insight panel */}
                            <div className="bg-gradient-to-br from-indigo-50 via-sky-50 to-cyan-50 dark:from-indigo-500/10 dark:via-sky-500/10 dark:to-cyan-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded-2xl p-5" data-testid="ai-insight-panel">
                                <div className="flex items-start gap-3">
                                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center flex-shrink-0 shadow">
                                        <Sparkles className="w-4 h-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] uppercase tracking-wider font-semibold text-indigo-500 mb-1">AI Insight</p>
                                        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-line">{insight}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Routing suggestions */}
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Route className="w-4 h-4 text-indigo-500" />
                                    <h3 className="text-sm font-semibold text-slate-700 dark:text-white">Routing options</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="routing-options">
                                    {ROUTING_OPTIONS.map((opt) => (
                                        <div key={opt.id} className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">
                                            <p className="font-semibold text-slate-800 dark:text-white text-sm">{opt.label}</p>
                                            <p className="text-xs text-slate-500 mt-1">{opt.description}</p>
                                            <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-300 mt-3">
                                                <span className="flex items-center gap-1"><Timer className="w-3 h-3" /> {opt.transitDays} days</span>
                                                <span className={`flex items-center gap-1 ${opt.costDelta < 0 ? 'text-emerald-600 dark:text-emerald-400' : opt.costDelta > 0 ? 'text-rose-600' : 'text-slate-500'}`}>
                                                    <DollarSign className="w-3 h-3" /> {opt.costDelta === 0 ? 'Baseline cost' : `${opt.costDelta > 0 ? '+' : ''}$${opt.costDelta}/container`}
                                                </span>
                                            </div>
                                            <div className="mt-3 flex flex-wrap gap-1">
                                                {opt.highlights.map((h, hIdx) => (
                                                    <span key={hIdx} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{h}</span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Book Now CTA */}
                            <div className="sticky bottom-4 bg-gradient-to-r from-indigo-500 via-sky-500 to-cyan-500 rounded-2xl p-4 flex items-center justify-between shadow-xl shadow-indigo-500/25" data-testid="book-now-bar">
                                <div className="text-white min-w-0">
                                    <p className="text-[11px] uppercase tracking-wider opacity-90">Ready to book</p>
                                    <p className="font-bold text-lg truncate">
                                        {selectedRate ? `${selectedRate.carrier} · ${selectedRate.type} · $${selectedRate.rate}/container · ${selectedRate.transitDays}d` : 'Select a rate to book'}
                                    </p>
                                </div>
                                <button
                                    onClick={() => selectedRate && toast.success(`Booking initiated with ${selectedRate.carrier} (${selectedRate.type}) at $${selectedRate.rate}/container`)}
                                    disabled={!selectedRate}
                                    data-testid="book-now-btn"
                                    className="px-5 py-2.5 rounded-xl bg-white text-indigo-600 hover:bg-slate-100 font-bold text-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed flex-shrink-0"
                                >
                                    <Rocket className="w-4 h-4" /> Book Now
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RateIntelligenceAgent;
